/* eslint-disable react/prop-types */
import { Button, ExternalLink, Spinner } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import { Formik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import StepModalHeader from '../../../components/StepModalHeader'
import { getSelectedCustomer } from '../../../../_augmentt/selectors/customers'
import useFreshEmployeeData from '../../../hooks/useFreshEmployeeData'
import { preTaskDefinitions, useTaskManagement, useTaskManagementMonitor } from '../../../util/task-management'
import { DEFAULT_SETTINGS_ACTIONS } from '../engage/constants'
import useDefaultSettings from '../engage/default-settings/useDefaultSettings'
import { FORM_FIELDS as SETTINGS_FIELDS } from '../engage/modals/OnboardUserDefaultSettings/constants'

function ManageLicensesModal(props) {
  const {
    show,
    onHide,
    onUpdate,
    intl,
    customer,
    employee,
    customerLicenses,
    isFetchingCustomerLicenses,
    syncCustomerLicenses,
    isSyncingCustomerLicenses,
  } = props

  // Global state
  const currentUser = useSelector(({ user }) => user.data)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const selectedCustomer = useSelector(getSelectedCustomer)

  const [selectedLicenses, setSelectedLicenses] = useState([])

  // Fetch fresh employee data when opened from table
  const freshEmployee = useFreshEmployeeData({
    employee,
    show,
    customer,
    isStaleData: (emp) => !(emp?.licenseType && Array.isArray(emp.licenseType)),
  })

  const employeeLicensesMap = useMemo(() => {
    const result = {}
    freshEmployee?.licenseType?.forEach((license) => {
      result[license.id] = true
    })
    return result
  }, [freshEmployee])

  const customerLicensesMap = useMemo(() => {
    const result = {}
    customerLicenses?.forEach((license) => {
      result[license.id] = license
    })
    return result
  }, [customerLicenses])

  const licensesToAdd = useMemo(
    () =>
      selectedLicenses
        .filter((license) => !employeeLicensesMap[license]) // if selected license was already assigned to employee ignore it
        .map((license) => customerLicensesMap[license] || {}),
    [customerLicensesMap, employeeLicensesMap, selectedLicenses],
  )

  const licensesToRemove = useMemo(
    () =>
      Object.keys(employeeLicensesMap)
        .filter((license) => !selectedLicenses.includes(license)) // if employee license is not in selected licenses, it was removed
        .map((license) => customerLicensesMap[license] || {}),
    [customerLicensesMap, employeeLicensesMap, selectedLicenses],
  )

  const [activeKey, setActiveKey] = useState('manage')

  const [isError, setError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [loadingButtonStyle, setLoadingButtonStyle] = useState({
    paddingLeft: '2.5rem',
    paddingRight: '2.5rem',
  })
  const [isSaving, setSaving] = useState(false)
  const [status, setStatus] = useState()

  // Put in your probe to en.json mappings here
  const errorMappings = {
    ADD_LIC_FAILED: 'ADD_LIC_FAILED',
    Request_ResourceNotFound: 'REQUEST_RESOURCE_NOT_FOUND',
  }

  const defaultSettings = useDefaultSettings({ action: DEFAULT_SETTINGS_ACTIONS.ONBOARDING })

  const disableLoading = () => {
    setIsLoading(false)
    setSaving(false)
    setLoadingButtonStyle({ paddingRight: '2.5rem', paddingLeft: '2.5rem' })
  }

  const update = () => {
    onUpdate()
  }

  const $tm = useTaskManagement({
    user: currentUser,
    customer: selectedCustomer,
    ...preTaskDefinitions.M365_ADD_USER,
  })
  const $tmm = useTaskManagementMonitor()

  const manageTaskResponse = async (taskResponse) => {
    const {
      data: { taskId: currentTaskId },
      data: { errorMessage },
    } = taskResponse

    try {
      if (errorMessage) {
        throw new Error(errorMessage)
      }

      const { result, error } = await $tmm.triggerTaskManagementMonitor(
        selectedCustomer.id,
        currentTaskId,
        errorMappings,
        intl,
      )

      return { result, error }
    } catch (error) {
      return { error: error.message }
    }
  }

  const manageResult = (result, error) => {
    if (error) {
      setError(true)
      setStatus(error)
    } else {
      setStatus(result)
      update()
    }
    disableLoading()
    syncCustomerLicenses()
  }

  useEffect(() => {
    if (isLoading && $tm.state.employeeIdentifier) {
      const trigger = async () => {
        await $tm.triggerTask(async (_, taskResponse) => {
          const { result, error } = await manageTaskResponse(taskResponse)
          manageResult(result, error)
        })
      }

      trigger()
    }
  }, [$tm.state.employeeIdentifier, isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = {}

  const enableLoading = () => {
    setIsLoading(true)
    setLoadingButtonStyle({ paddingRight: '3.5rem', paddingLeft: '2.5rem' })
  }

  async function manageLicenses() {
    const addLicenseIds = []
    // eslint-disable-next-line no-restricted-syntax
    for (const license of licensesToAdd) {
      addLicenseIds.push(license.id)
    }

    const removeLicenseIds = []
    // eslint-disable-next-line no-restricted-syntax
    for (const license of licensesToRemove) {
      removeLicenseIds.push(license.id)
    }

    const parameters = {
      employeeIdentifier: freshEmployee?.employeeIdentifier || employee?.employeeIdentifier,
      firstName: freshEmployee?.firstName || employee?.firstName,
      lastName: freshEmployee?.lastName || employee?.lastName,
      displayName: freshEmployee?.displayName || employee?.displayName || undefined,
      email: freshEmployee?.email || employee?.email,
      department: freshEmployee?.department || employee?.department,
      licenseInfo: {
        ...$tm.state.licenseInfo,
        usageLocation: undefined,
        toBeAdded: addLicenseIds,
        toBeRemoved: removeLicenseIds,
      },
    }

    enableLoading()

    $tm.setState({ ...parameters })
    setSaving(true)
  }

  /**
   * Update licenses to be added
   * @param {*} index
   */
  const updateLicenses = (license, checked) => {
    if (checked) {
      setSelectedLicenses([...selectedLicenses, license.id])
    } else {
      setSelectedLicenses(selectedLicenses.filter((item) => item !== license.id))
    }
  }

  const hide = () => {
    setSelectedLicenses([])
    setError(false)
    onHide()
    setStatus()
  }

  // Next button click handler
  const next = (values) => {
    if (activeKey === 'manage') {
      setActiveKey('review')
    } else {
      manageLicenses(values)
    }
  }

  // Track current employee licenses for change detection
  const currentEmployeeLicenses = useMemo(
    () => Object.keys(employeeLicensesMap).sort().join(','),
    [employeeLicensesMap],
  )

  // Reset selections when licenses change
  useEffect(() => {
    if (show && employeeLicensesMap) {
      setActiveKey('manage')
      setSelectedLicenses(Object.keys(employeeLicensesMap))
      setStatus()
      setError(false)
    }
  }, [show, currentEmployeeLicenses, freshEmployee, employeeLicensesMap])

  const licenseSteps = useMemo(
    () => [
      { key: 'manage', label: <FormattedMessage id="ENGAGE.MANAGE" /> },
      { key: 'review', label: <FormattedMessage id="ENGAGE.REVIEW_AND_FINISH" /> },
    ],
    [],
  )

  const currentLicenseStepIndex = activeKey === 'review' ? 1 : 0

  return (
    <Modal
      size="md"
      backdrop={isLoading ? 'static' : true}
      id="manageLicensesModal"
      className={`engage-modal right ${clsx({ in: show })}`}
      aria-labelledby="manage-licenses-modal"
      show={show}
      onHide={hide}
      onSubmit={next}
    >
      <Formik enableReinitialize initialValues={initialValues} onSubmit={(values) => next(values)}>
        {({ handleSubmit }) => (
          <Form>
            <Modal.Header className="grey-bg-color-1" style={{ paddingBottom: '1rem' }} closeButton={!isLoading}>
              <div className="w-100 pe-4">
                <Modal.Title className="mt-4">
                  <FormattedMessage id="ENGAGE.MANAGE_LICENSES" />
                </Modal.Title>
                <p className="mt-2">
                  {freshEmployee?.firstName || employee?.firstName} {freshEmployee?.lastName || employee?.lastName}
                </p>
                <p>
                  {selectedIntegration?.IntegrationApplication?.display_name === 'M365'
                    ? 'Microsoft 365'
                    : selectedIntegration?.IntegrationApplication?.display_name}
                </p>
                {!isLoading && !status && (
                  <div className="pb-2 pt-1">
                    <StepModalHeader steps={licenseSteps} stepIndex={currentLicenseStepIndex} variant="engage" />
                  </div>
                )}
              </div>
            </Modal.Header>
            <Modal.Body>
              {!isLoading && !status && activeKey === 'manage' && (
                <div className="section">
                  <Row className="justify-content-center align-items-center">
                    <Col sm="12" md="12" lg="12">
                      <div className="text-strong mb-4 d-flex align-items-baseline">
                        <FormattedMessage id="ENGAGE.MANAGE_LICENSE" />

                        <Button
                          variant="link"
                          className="p-0 ms-2"
                          onClick={syncCustomerLicenses}
                          disabled={isSyncingCustomerLicenses || isFetchingCustomerLicenses}
                        >
                          <i className={clsx('fa fa-rotate', isSyncingCustomerLicenses && 'fa-spin')} />
                        </Button>
                      </div>

                      {isFetchingCustomerLicenses && (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <Spinner size="small" />
                          <div className="mt-3 text-bold text-color-secondary">
                            <FormattedMessage id="ENGAGE.ONBOARD.LOADING_LICENSES" />
                          </div>
                        </div>
                      )}

                      {!isFetchingCustomerLicenses && (
                        <Form.Group id="licenses" className="mb-0 ms-4">
                          {customerLicenses.map((license, index) => (
                            <Form.Check
                              custom
                              type="checkbox"
                              className="checkbox-left-aligned"
                              checked={selectedLicenses.includes(license.id)}
                              disabled={
                                !employeeLicensesMap[license.id] && // if employee originally has this license do not disable field
                                license.availableLicences <= 0
                              }
                              id={`license-${index}`}
                              // eslint-disable-next-line react/no-array-index-key
                              key={`license-${index}`}
                              label={
                                <>
                                  <span className="font-bolder">{license.displayName}</span>
                                  <p className="font-color-2">
                                    {license.availableLicences > 0
                                      ? intl.formatMessage(
                                          { id: 'ENGAGE.LICENSES_AVAILABLE' },
                                          {
                                            available: license.availableLicences,
                                            total: license.totalLicences,
                                          },
                                        )
                                      : intl.formatMessage({
                                          id: 'ENGAGE.NO_LICENSES_AVAILABLE',
                                        })}
                                  </p>
                                </>
                              }
                              onChange={(e) => {
                                updateLicenses(license, e.target.checked)
                              }}
                            />
                          ))}
                        </Form.Group>
                      )}

                      {defaultSettings[SETTINGS_FIELDS.DISPLAY_PURCHASE_LINK] && (
                        <div className="text-bold ms-4">
                          <ExternalLink
                            href={defaultSettings[SETTINGS_FIELDS.PURCHASE_LINK]}
                            label={<FormattedMessage id="ENGAGE.ONBOARD.PURCHASE_LICENSES" />}
                          />
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
              {!isLoading && !status && activeKey === 'review' && (
                <Row>
                  <Col sm="12" md="12" lg="12">
                    {licensesToAdd?.length ? (
                      <>
                        <p className="font-bolder font-lg mb-2">
                          <FormattedMessage id="ENGAGE.LICENSES_TO_BE_ASSIGNED" />
                        </p>
                        {licensesToAdd.map((license, index) => (
                          <p key={license.id || license.displayName || `license-to-add-${index}`} className="mb-0">
                            {license.displayName}
                          </p>
                        ))}
                        <Button variant="link" className="mb-4 ps-0 pe-0" onClick={() => setActiveKey('manage')}>
                          <span>
                            <FormattedMessage id="GENERAL.EDIT" />
                          </span>
                        </Button>
                      </>
                    ) : undefined}

                    {licensesToRemove?.length ? (
                      <>
                        <p className="font-bolder font-lg mb-2">
                          <FormattedMessage id="ENGAGE.LICENSES_TO_BE_REMOVED" />
                        </p>
                        {licensesToRemove.map((license, index) => (
                          <p key={license.id || license.displayName || `license-to-remove-${index}`} className="mb-0">
                            {license.displayName}
                          </p>
                        ))}
                        <Button variant="link" className="mb-4 ps-0 pe-0" onClick={() => setActiveKey('manage')}>
                          <span>
                            <FormattedMessage id="GENERAL.EDIT" />
                          </span>
                        </Button>
                      </>
                    ) : undefined}
                  </Col>
                </Row>
              )}

              {!isLoading && isError && (
                <Row>
                  <Col sm="12" md="12" lg="12">
                    <span className="icon-lg font-color-danger fas fa-circle-xmark" />
                    <span className="ms-3">
                      <FormattedMessage id="ENGAGE.ADD_LICENSE" />
                    </span>
                    <p className="font-color-danger" style={{ marginLeft: '2.5rem' }}>
                      {status?.StatusMessage ? status?.StatusMessage : intl.formatMessage({ id: 'GENERAL.ERROR' })}
                    </p>
                  </Col>
                </Row>
              )}
              {!isLoading && !isError && status && (
                <Row>
                  <Col sm="12" md="12" lg="12">
                    {licensesToAdd.length ? (
                      <>
                        <span className="icon-lg font-color-engage fas fa-circle-check" />
                        <span className="ms-3">
                          <FormattedMessage id="ENGAGE.LICENSES_ASSIGNED" />
                        </span>
                        <ul className="mb-4">
                          {licensesToAdd.map((license, index) => (
                            <li
                              key={license.id || license.displayName || `license-added-${index}`}
                              className="mb-0"
                              style={{ marginLeft: '0.5rem' }}
                            >
                              {license.displayName}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : undefined}
                    {licensesToRemove.length ? (
                      <>
                        <span className="icon-lg font-color-engage fas fa-circle-check" />
                        <span className="ms-3">
                          <FormattedMessage id="ENGAGE.LICENSES_REMOVED" />
                        </span>
                        <ul>
                          {licensesToRemove.map((license, index) => (
                            <li
                              key={license.id || license.displayName || `license-removed-${index}`}
                              className="mb-0"
                              style={{ marginLeft: '0.5rem' }}
                            >
                              {license.displayName}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : undefined}
                  </Col>
                </Row>
              )}
              {isLoading && customerLicenses.length ? (
                <Row>
                  <Col sm="12" md="12" lg="12">
                    <div className="centered">
                      <Spinner />
                      {isSaving && (
                        <p className="font-lg font-bolder font-color-2">
                          <FormattedMessage id="GENERAL.SAVING_CHANGES" />
                        </p>
                      )}
                    </div>
                  </Col>
                </Row>
              ) : undefined}
            </Modal.Body>

            {!isLoading && (
              <Modal.Footer className="bottom">
                <Button
                  onClick={hide}
                  variant="light"
                  className={`${clsx({
                    'pull-left': !status,
                    'pull-right': status,
                  })}`}
                >
                  <FormattedMessage id="GENERAL.CLOSE" />
                </Button>
                {!status && (
                  <Button
                    id={activeKey === 'review' ? 'manage-licenses-submit-btn' : 'nextBtn'}
                    onClick={handleSubmit}
                    disabled={isLoading || (!licensesToAdd.length && !licensesToRemove.length)}
                    variant="secondary"
                    className="bg-color-engage pull-right"
                    style={loadingButtonStyle}
                  >
                    {activeKey === 'review' ? (
                      <FormattedMessage id="GENERAL.SAVE_CHANGES" />
                    ) : (
                      <FormattedMessage id="GENERAL.NEXT" />
                    )}
                  </Button>
                )}
              </Modal.Footer>
            )}
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default injectIntl(ManageLicensesModal)
